import type { ItemCacheLockId } from 'owa-items-cache';
import { fetchTasks } from './fetchTasks';
import type { Todo, Subtodo } from 'owa-todo-types';
import { getUpsertDescriptor } from './updateDescriptorForUpsert';
import { getTodoCache } from '../todoCache';
import type { TodoDescriptor } from '../schema/TodoDescriptor';
import { getDescriptorId } from './filter/getDescriptorId';
import {
    subscribeNotifications,
    unSubscribeNotifications,
    subscribePolling,
    unSubscribePolling,
} from 'owa-todo-notifications';
import { handleNotification } from './handleNotification';
import { getCompareForDescriptor, getSubtodoCompareForDescriptor } from './getCompareForDescriptor';
import { reloadTodos } from './reloadTodos';
import { isTodoFolderDescriptor } from './typeGuards';
import { getLoadState, TasksLoadState } from 'owa-todo-cache-load-state';

export function getTodoCacheLock(lockId: ItemCacheLockId) {
    return {
        fetchTasks: (descriptor: TodoDescriptor, forceReload: boolean): Promise<Todo[]> => {
            return fetchTasks(lockId, descriptor, forceReload);
        },
        getTasksLoadState: (descriptor: TodoDescriptor): TasksLoadState => {
            // The descriptorId (NOT the upsert descriptorId) is used to track the
            // progress of requests. This is because we do not have the information for the
            // upsert descriptor until we recieve the request response.
            const id = getDescriptorId(descriptor);
            return getLoadState(id);
        },
        getItemsForLock: (descriptor: TodoDescriptor): Todo[] | undefined => {
            const descriptorAdjusted = getUpsertDescriptor(descriptor);
            const descriptorId = getDescriptorId(descriptorAdjusted);
            const compareFunc = getCompareForDescriptor(descriptorAdjusted);
            const subtodoCompareFunc = getSubtodoCompareForDescriptor(descriptorAdjusted);
            const sortedTodos = getTodoCache()
                .getItemsForLock(lockId, { ...descriptorAdjusted, Id: descriptorId })
                ?.sort(compareFunc);
            if (sortedTodos && subtodoCompareFunc) {
                return sortedTodos.map(todo =>
                    !!todo.Subtasks ? copyAndSortSubtodos(todo, subtodoCompareFunc) : todo
                );
            }
            return sortedTodos;
        },
        getTaskForLock: (id: string, descriptor?: TodoDescriptor): Todo | undefined => {
            const originalTodo = getTodoCache().selectItemByIdForLock(lockId, id);
            if (descriptor && originalTodo?.Subtasks) {
                const descriptorAdjusted = getUpsertDescriptor(descriptor);
                const subtodoCompareFunc = getSubtodoCompareForDescriptor(descriptorAdjusted);
                const todoCopy = copyAndSortSubtodos(originalTodo, subtodoCompareFunc);
                return todoCopy;
            }
            return originalTodo;
        },
        taskFolderAdded: (descriptor: TodoDescriptor) => {
            if (isTodoFolderDescriptor(descriptor)) {
                const upsertDescriptorId = getDescriptorId(descriptor);
                const cacheDescriptor = { ...descriptor, Id: upsertDescriptorId };
                getTodoCache().upsertItemsForDescriptor(lockId, [], cacheDescriptor);
            }
        },
        subscribeNotifications: () => {
            subscribeNotifications(lockId, {
                callback: handleNotification,
                callbackId: 'processTasks',
            });
            subscribePolling(lockId, {
                callback: reloadTodos,
                callbackId: 'reloadTodos',
            });
        },
        releaseLock: () => {
            unSubscribeNotifications(lockId);
            unSubscribePolling(lockId);
        },
    };
}

/**
 * Copies the original todo and corresponding subtodos, then sorts the subtodos.
 * Copying is necessary since the original objects are observable store objects and cannot be
 * mutated unless through a mutator.
 * @param originalTodo original Todo item that will have its subtodos sorted
 * @param compareFunc sort compare function
 */
function copyAndSortSubtodos(
    originalTodo: Todo,
    compareFunc: (a: Subtodo, b: Subtodo) => number
): Todo {
    const todoCopy = { ...originalTodo };
    const subtodosCopy = [...originalTodo.Subtasks];
    subtodosCopy.sort(compareFunc);
    todoCopy.Subtasks = subtodosCopy;
    return todoCopy;
}
